import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppHeader } from '@/components/AppHeader';
import { AuthGuard } from '@/components/AuthGuard';
import { OfflineBanner } from '@/components/OfflineBanner';
import { EditorShell } from '@/features/editor';
import { NotFoundPage } from '@/routes/NotFoundPage';
import { Skeleton } from '@pptcreate/ui';

const GeneratePage = lazy(() =>
  import('@/routes/GeneratePage').then((module) => ({ default: module.GeneratePage })),
);
const DecksPage = lazy(() =>
  import('@/routes/DecksPage').then((module) => ({ default: module.DecksPage })),
);
const EditorPage = lazy(() =>
  import('@/routes/EditorPage').then((module) => ({ default: module.EditorPage })),
);
const TemplatesPage = lazy(() =>
  import('@/routes/TemplatesPage').then((module) => ({ default: module.TemplatesPage })),
);
const LoginPage = lazy(() =>
  import('@/routes/LoginPage').then((module) => ({ default: module.LoginPage })),
);

function PageFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center px-6">
      <Skeleton className="h-8 w-48" />
    </div>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-brut-bg text-brut-fg font-mono antialiased">
      <OfflineBanner />
      <AppHeader />
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </div>
  );
}

export default function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/generate"
          element={
            <AuthGuard>
              <AppLayout>
                <GeneratePage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/decks"
          element={
            <AuthGuard>
              <AppLayout>
                <DecksPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/editor/:deckId"
          element={
            <AuthGuard>
              <EditorShell>
                <OfflineBanner />
                <Suspense fallback={<PageFallback />}>
                  <EditorPage />
                </Suspense>
              </EditorShell>
            </AuthGuard>
          }
        />
        <Route
          path="/templates"
          element={
            <AuthGuard>
              <AppLayout>
                <TemplatesPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route path="/" element={<Navigate to="/generate" replace />} />
        <Route
          path="*"
          element={
            <AppLayout>
              <NotFoundPage />
            </AppLayout>
          }
        />
      </Routes>
    </Suspense>
  );
}
